'use client'

import { useDotEnv } from '@repo/dot-env/client'
import { LocationPlaceValue } from '@repo/features/location'
import { getAddressForCoordinates, getPlaceDetails, PastedLocationResult } from '@repo/geolocation'
import {
  buildCompanyLocationValuesFromCoordinates,
  buildCompanyLocationValuesFromGooglePlace,
  companyLocationDefaultValues,
  CompanyLocationFormValues,
} from '@repo/form/location'
import type { ResponsiveSheetPresentation } from '@repo/ui/components'

import { useCreateLocationSheet } from '@/app/@protected/_components/create-location'

export function useLoadLocationHandlers() {
  const { GEOLOCATION_SERVICE_URL } = useDotEnv()
  const { openCreateLocationSheet, openEditLocationSheet } = useCreateLocationSheet()

  const handleCreateLocation = async ({
    companyId: targetCompanyId,
    googlePlace,
    onCreated,
    orgIdOverride,
    pastedLocation,
    presentation,
    query,
  }: {
    companyId: number
    googlePlace?: { place_id: string; place_name: string }
    onCreated: (_place: LocationPlaceValue) => void
    orgIdOverride?: number
    pastedLocation?: PastedLocationResult
    presentation?: ResponsiveSheetPresentation
    query: string
  }) => {
    let initialValues: Partial<CompanyLocationFormValues> | undefined

    if (pastedLocation) {
      if (pastedLocation.kind === 'google-location') {
        const { coordinates, googlePlace: pastedGooglePlace } = pastedLocation

        const details = await getPlaceDetails(pastedGooglePlace.place_id, GEOLOCATION_SERVICE_URL)
        initialValues =
          details.status === 'found'
            ? buildCompanyLocationValuesFromGooglePlace(details.value, pastedGooglePlace)
            : { name: pastedGooglePlace.place_name }

        if (coordinates) {
          initialValues = {
            ...initialValues,
            address: {
              ...companyLocationDefaultValues.address,
              ...initialValues?.address,
              lat: String(coordinates.lat),
              lng: String(coordinates.lng),
            },
          }
        }
      } else {
        const { coordinates } = pastedLocation

        const address = await getAddressForCoordinates(coordinates, GEOLOCATION_SERVICE_URL)
        initialValues = buildCompanyLocationValuesFromCoordinates(
          coordinates,
          address.status === 'found' ? address.value.address : undefined,
        )
      }
    } else if (googlePlace) {
      const details = await getPlaceDetails(googlePlace.place_id, GEOLOCATION_SERVICE_URL)
      initialValues =
        details.status === 'found'
          ? buildCompanyLocationValuesFromGooglePlace(details.value, googlePlace)
          : { name: query }
    } else {
      initialValues = { name: query }
    }

    openCreateLocationSheet({
      companyId: targetCompanyId,
      initialSearchQuery: query,
      initialValues,
      mode: 'create',
      onCreated,
      orgIdOverride,
      presentation,
    })
  }

  const handleEditLocation = ({
    companyId: targetCompanyId,
    locationId,
    onUpdated,
    orgIdOverride,
    presentation,
  }: {
    companyId: number
    locationId: number
    onUpdated: (_place: LocationPlaceValue) => void
    orgIdOverride?: number
    presentation?: ResponsiveSheetPresentation
  }) => {
    openEditLocationSheet({
      companyId: targetCompanyId,
      locationId,
      mode: 'edit',
      onUpdated,
      orgIdOverride,
      presentation,
    })
  }

  return { handleCreateLocation, handleEditLocation }
}
